import React, { useEffect, useMemo, useState } from 'react'
import { Form, Formik } from 'formik'
import { addBadgeSubjectSchema } from 'schemas/badges'
import { Button, CircularProgress, Grid, makeStyles, Typography } from '@material-ui/core'
import CustomTextField from 'components/Form/CustomTextField'
import { Box, Stack } from '@mui/material'
import ProgressButton from 'components/ProgressButton'
import Wrapper from 'components/Wrapper'
import { badgesSubjectsApplication } from 'constants/index'
import CustomSelect from 'components/Form/CustomSelect'
import badgesSlice, {
  addBadgeSubject,
  badgeCounterExists,
  getBadgeSubject,
  getHighestBadgeCounter,
  updateBadgeSubject
} from 'store/badges'
import { useDispatch, useSelector } from 'react-redux'
import { debounce } from 'lodash'
import { Alert } from '@material-ui/lab'

const BadgeSubjectForm = (props) => {
  const { onClose, subjectId } = props
  const dispatch = useDispatch()
  const classes = useStyles()

  const [addingSubject, setAddingSubject] = useState(false)
  const [loadingSubject, setLoadingSubject] = useState(false)
  const [lastCounter, setLastCounter] = useState(0)
  const [isBadgeCounterExists, setIsBadgeCounterExists] = useState(false)
  const [isCheckingCounter, setIsCheckingCounter] = useState(false)
  const [newBadgeCounter, setNewBadgeCounter] = useState(0)

  const { conference } = useSelector((state) => state.conference)
  const { badgeSubject } = useSelector((state) => state.badges)

  useEffect(() => {
    const getData = async () => {
      setLoadingSubject(true)
      await dispatch(getBadgeSubject(subjectId))
      setLoadingSubject(false)
    }
    if (subjectId && conference?.id) {
      getData(subjectId)
    }

    return () => {
      dispatch(badgesSlice.actions.setBadgeSubject(null))
    }
  }, [subjectId, conference?.id])

  const initialValues = {
    counter: badgeSubject?.counter ?? lastCounter + 1 ?? '',
    Type: badgeSubject?.Type ?? '',
    FullName: badgeSubject?.FullName ?? '',
    Credential: badgeSubject?.Credential ?? '',
    Position_Title: badgeSubject?.['Position_Title'] ?? '',
    Organization: badgeSubject?.Organization ?? '',
    Phone: badgeSubject?.Phone ?? '',
    Email: badgeSubject?.Email ?? '',
    City: badgeSubject?.City ?? '',
    State: badgeSubject?.State ?? '',
    Country: badgeSubject?.Country ?? ''
  }

  const debouncedCheckLastBadge = useMemo(
    () =>
      debounce(async (value) => {
        if (parseInt(value) === initialValues.counter) {
          setIsBadgeCounterExists(false)
          return
        }
        setIsCheckingCounter(true)
        const counterExists = await badgeCounterExists(value, conference?.id)
        setIsBadgeCounterExists(counterExists)
        setIsCheckingCounter(false)
      }, 1000),
    [initialValues.counter]
  )

  useEffect(() => {
    const getLastBadgeData = async (conferenceId) => {
      setLoadingSubject(true)
      const lastBadge = await getHighestBadgeCounter(conferenceId)
      if (lastBadge) setLastCounter(lastBadge?.counter)
      setLoadingSubject(false)
    }
    if (conference?.id) {
      getLastBadgeData(conference?.id)
    }
  }, [conference?.id])

  const handleSubmit = async (values) => {
    setAddingSubject(true)
    const payload = { ...values, counter: parseInt(values.counter) }
    const counterExists = await badgeCounterExists(payload.counter, conference?.id)
    if (counterExists) {
      const lastBadge = await getHighestBadgeCounter(conference?.id)
      if (lastBadge) setNewBadgeCounter(lastBadge?.counter)
      setIsBadgeCounterExists(counterExists)
      setAddingSubject(false)
      return
    }
    if (subjectId) {
      await updateBadgeSubject(payload, subjectId, conference?.id, onClose)
      setAddingSubject(false)
    } else {
      await addBadgeSubject(payload, conference?.id, onClose)
      setAddingSubject(false)
    }
  }

  return (
    <Box>
      <Wrapper inProgress={loadingSubject}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={addBadgeSubjectSchema}
          onSubmit={handleSubmit}>
          {({ setFieldValue, values }) => (
            <Form className={classes.form}>
              <Grid container alignItems="center" spacing={3}>
                <Grid item xs={12} md={4}>
                  <div className={classes.counterBox}>
                    {isCheckingCounter && (
                      <span className={classes.circularProgress}>
                        <CircularProgress size={18} />
                      </span>
                    )}
                    <CustomTextField
                      label="Counter"
                      name="counter"
                      type="number"
                      onChange={async (e) => {
                        const value = e.target.value
                        setFieldValue('counter', value)
                        await debouncedCheckLastBadge(value)
                      }}
                    />
                  </div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomSelect
                    inputLabel="Type"
                    options={badgesSubjectsApplication?.map((item) => ({
                      id: item,
                      type: item
                    }))}
                    optionAttr="type"
                    name="Type"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Full Name" name="FullName" type="text" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Credential" name="Credential" type="text" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Position/Title" name="Position_Title" type="text" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Organization" name="Organization" type="text" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Phone" name="Phone" type="text" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Email" name="Email" type="email" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="City" name="City" type="text" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="State" name="State" type="text" />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField label="Country" name="Country" type="text" />
                </Grid>
              </Grid>

              {isBadgeCounterExists && (
                <Box marginTop={2}>
                  <Alert
                    severity="warning"
                    action={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Button
                          color="default"
                          variant="outlined"
                          size="small"
                          onClick={() => setIsBadgeCounterExists(false)}>
                          Yes
                        </Button>
                        <Button
                          color="default"
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            if (lastCounter + 1 === values.counter) {
                              setFieldValue('counter', newBadgeCounter + 1)
                              setLastCounter(newBadgeCounter)
                              setIsBadgeCounterExists(false)
                              return
                            }
                            setFieldValue('counter', lastCounter + 1)
                            setIsBadgeCounterExists(false)
                          }}>
                          No
                        </Button>
                      </Box>
                    }>
                    <Typography className={classes.listText}>
                      A Badge with this counter already exists.
                    </Typography>
                    <ul>
                      <li className={classes.listText}>
                        Click <strong>Yes</strong> to keep the badges with the same counter.
                      </li>
                      <li className={classes.listText}>
                        Click <strong>No</strong> to update the counter to the next available number
                        (Highest badge counter + 1).
                      </li>
                    </ul>
                  </Alert>
                </Box>
              )}

              <Stack direction="row" spacing={1} className={classes.actions}>
                <Button size="large" onClick={onClose} disabled={addingSubject}>
                  Cancel
                </Button>
                <ProgressButton
                  type="submit"
                  label="Save"
                  inProgress={addingSubject}
                  disabled={addingSubject || isBadgeCounterExists || isCheckingCounter}
                />
              </Stack>
            </Form>
          )}
        </Formik>
      </Wrapper>
    </Box>
  )
}

export default BadgeSubjectForm

const useStyles = makeStyles({
  header: {
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0px',
    marginBottom: '16px'
  },
  actions: {
    justifyContent: 'end',
    margin: '16px 0px'
  },
  listText: {
    fontSize: '15px'
  },
  counterBox: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  'absolute inset-y-0 right-0 flex items-center pr-3': {},
  circularProgress: {
    position: 'absolute',
    right: 0,
    paddingRight: '36px',
    marginTop: '4px'
  }
})
